import React, { useState } from 'react'
import { useDispatch, useSelector } from 'hooks/use-redux'
import { Dialog, Flex, Button, TextField, Text } from 'components/ds'
import {
  createBriefProject,
  getBriefProjectsLoading,
  getBriefProjectsError,
} from '@disco/state-management/reducers/entities/briefProjects'
import { switchPaneView } from '@disco/state-management/actions/ExplorerActions'
import { getExplorerPanes } from '@disco/state-management/selectors/explorer'
import { BRIEF_LIST_PANE } from '@disco/constants/PaneTypes'
import { BRIEF_ALL_VIEW } from '@disco/constants/ViewTypes'
import captureError from '@disco/utils/captureError'
import mixpanel from '@disco/api/mixpanel'

interface CreateNewProjectModalProps {
  isOpen: boolean
  onClose: () => void
}

export const CreateNewProjectModal: React.FC<CreateNewProjectModalProps> = ({
  isOpen,
  onClose,
}) => {
  const dispatch = useDispatch()
  const isCreating = useSelector(getBriefProjectsLoading)
  const panes = useSelector(getExplorerPanes)
  const [error, setError] = useState<string | null>(null)
  const [projectName, setProjectName] = useState('')

  const handleCreate = async () => {
    if (!projectName.trim()) return

    try {
      await dispatch(createBriefProject(projectName.trim())).unwrap()
      mixpanel.sendPureEvent('Project created', { Context: 'Briefs' })
      // Reset brief filters to "All" view and clear search after creating a project
      const briefPane = panes.find(pane => pane.type === BRIEF_LIST_PANE)
      if (
        briefPane &&
        (briefPane.view !== BRIEF_ALL_VIEW || briefPane.searchTerm)
      ) {
        dispatch(switchPaneView(briefPane, BRIEF_ALL_VIEW, { searchTerm: '' }))
      }

      setProjectName('')
      setError(null)
      onClose()
    } catch (error) {
      const unwrappedError = (error as { error: string }).error
      setError(unwrappedError)
      captureError(new Error(unwrappedError), {
        description: 'Failed to create project',
      })
    }
  }

  const handleCancel = () => {
    setProjectName('')
    setError(null)
    onClose()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleCreate()
    }
  }

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleCancel}>
      <Dialog.Content maxWidth="500px" aria-describedby={undefined}>
        <Dialog.Title>Create new brief project</Dialog.Title>

        <Flex direction="column" gap="4" mt="4">
          <div>
            <Text as="label" size="2" weight="medium">
              Name
            </Text>
            <TextField.Root
              mt="1"
              value={projectName}
              onChange={e => setProjectName(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isCreating}
              required
            />
            {error && (
              <Text size="2" color="red" mt="1">
                {error}
              </Text>
            )}
          </div>
        </Flex>

        <Flex gap="3" mt="4" justify="end">
          <Dialog.Close>
            <Button
              variant="soft"
              color="gray"
              onClick={handleCancel}
              disabled={isCreating}
            >
              Cancel
            </Button>
          </Dialog.Close>
          <Button
            onClick={handleCreate}
            disabled={!projectName.trim() || isCreating}
            loading={isCreating}
          >
            Create
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  )
}
